import { useEffect, useMemo, useState, useRef } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { Box, useMediaQuery } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateValidationError } from '@mui/x-date-pickers/models'
import { TDateRange } from 'types/models/DataAnalysis'
import * as sxStyles from './CustomeDatePicker.styled'

const HELPER_MIN_HEIGHT = 40

interface IDatePicker {
  startDate: string
  endDate: string
  handleDateChange: (filters: TDateRange) => void
}

// a safe parser for initial values.
const parseOrNull = (v: string): Dayjs | null => {
  if (!v) return null
  const d = dayjs(v)
  return d.isValid() ? d : null
}

const CustomeDatePicker = ({
  startDate,
  endDate,
  handleDateChange,
}: IDatePicker) => {
  const today = useMemo(() => dayjs(), [])
  const isMobile = useMediaQuery('(max-width:600px)')

  const [startValue, setStartValue] = useState<Dayjs | null>(
    parseOrNull(startDate)
  )
  const [endValue, setEndValue] = useState<Dayjs | null>(parseOrNull(endDate))

  const [startError, setStartError] = useState<string | null>(null)
  const [endError, setEndError] = useState<string | null>(null)

  // This is needed instead of including handleDateChange into the ref, which broke the navigation
  const handleDateChangeRef = useRef(handleDateChange)
  handleDateChangeRef.current = handleDateChange

  const isUserChangeRef = useRef(false)

  const mapError = (reason: DateValidationError | null): string | null => {
    switch (reason) {
      case 'maxDate':
        return 'Start date cannot be after end date'
      case 'minDate':
        return 'End date cannot be before start date'
      case 'invalidDate':
        return 'Invalid date'
      default:
        return null
    }
  }

  useEffect(() => {
    if (isUserChangeRef.current) {
      isUserChangeRef.current = false
      return
    }

    const newStartValue = parseOrNull(startDate)
    const newEndValue = parseOrNull(endDate)

    const startChanged = startValue?.format('YYYY-MM-DD') !== startDate
    const endChanged = endValue?.format('YYYY-MM-DD') !== endDate

    if (startChanged) {
      setStartValue(newStartValue)
    }
    if (endChanged) {
      setEndValue(newEndValue)
    }
  }, [startDate, endDate])

  // Notify parent of internal changes
  useEffect(() => {
    const toYMD = (d: Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '')
    const formattedStart = toYMD(startValue)
    const formattedEnd = toYMD(endValue)

    // Only call handleDateChange if values differ from props
    if (formattedStart !== startDate || formattedEnd !== endDate) {
      isUserChangeRef.current = true
      handleDateChangeRef.current({
        from: formattedStart,
        to: formattedEnd,
      })
    }
  }, [startValue, endValue, startDate, endDate])

  return (
    <Box sx={sxStyles.boxWrapper}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={sxStyles.datePickerBoxWrapper}>
          <DatePicker
            views={['year', 'month', 'day']}
            openTo="day"
            format="DD/MM/YYYY"
            label="From"
            value={startValue}
            disableFuture
            maxDate={endValue ?? today}
            onChange={(newStartValue) => setStartValue(newStartValue)}
            onError={(reason) => setStartError(mapError(reason))}
            slotProps={{
              textField: {
                size: isMobile ? 'small' : 'medium',
                error: !!startError,
                helperText: startError || ' ',
                sx: {
                  '& .MuiFormHelperText-root': {
                    height: HELPER_MIN_HEIGHT,
                    margin: 0,
                    display: 'flex',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                  },
                },
              },
            }}
          />
          <br />
          <DatePicker
            views={['year', 'month', 'day']}
            openTo="day"
            format="DD/MM/YYYY"
            label="To"
            value={endValue}
            disableFuture
            minDate={startValue ?? undefined}
            onChange={(newEndValue) => setEndValue(newEndValue)}
            onError={(reason) => setEndError(mapError(reason))}
            slotProps={{
              textField: {
                size: isMobile ? 'small' : 'medium',
                error: !!endError,
                helperText: endError || ' ',
                sx: {
                  '& .MuiFormHelperText-root': {
                    height: HELPER_MIN_HEIGHT,
                    margin: 0,
                    display: 'flex',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                  },
                },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  )
}

export default CustomeDatePicker
